import { useState } from 'react';
import { Icon } from '@frontend/icons';
import { ConfirmationModal, ContentLoader, Table, useModalControl } from '@frontend/design-system';

interface DownloadLinks {
  linux?: string;
  mac?: string;
  win32?: string;
}
interface S3File {
  key: string;
  name: string;
  size: number;
  lastModified: Date;
  url?: string;
}

const getAssetOs = (asset: S3File): string => {
  if (asset.key?.includes('darwin')) return 'darwin';
  if (asset.key?.includes('win32')) return 'win32';
  if (asset.key?.includes('linux')) return 'linux';
  return 'unknown';
};

export const ReleaseDetails = ({
  loading,
  version,
  versions,
  deleteFile,
  handleDownload,
}: {
  loading: boolean;
  version: string;
  versions: null | {
    version: string;
    assets: {
      darwin: S3File[];
      win32: S3File[];
      linux: S3File[];
    };
  };
  deleteFile: (key: string, currentVersion: string) => Promise<void>;
  handleDownload: (platform: 'mac' | 'win32' | 'linux', downloadLinks?: DownloadLinks | string) => Promise<void>;
}) => {
  if (!versions?.assets) {
    return <span>No assets found</span>;
  }

  return loading ? (
    <ContentLoader show={true} />
  ) : (
    <ReleaseTable
      version={version}
      assets={[...versions.assets.darwin, ...versions.assets.linux, ...versions.assets.win32]}
      handleDownload={handleDownload}
      handleDelete={deleteFile}
    />
  );
};

const ReleaseTable = ({
  assets,
  version,
  handleDelete,
  handleDownload,
}: {
  assets: S3File[];
  version: string;
  handleDelete: (key: string, currentVersion: string) => Promise<void>;
  handleDownload: (platform: 'mac' | 'win32' | 'linux', downloadLinks?: DownloadLinks | string) => Promise<void>;
}) => {
  const { modalProps, openModal, closeModal } = useModalControl();
  const [deletingAsset, setDeletingAsset] = useState('');
  return (
    <>
      <Table
        data={assets ?? []}
        colConfig={[
          {
            Header: 'Name',
            headerAlign: 'center',
            id: 'name',
            accessor: (asset) => (
              <div style={{ textAlign: 'center', width: '100%' }}>{!asset.name ? 'N/A' : asset.name}</div>
            ),
            minWidth: 100,
          },
          {
            Header: 'Last Modified Date',
            headerAlign: 'center',
            id: 'publishedAt',
            accessor: (asset) => (
              <div style={{ textAlign: 'center', width: '100%' }}>
                {!asset.lastModified ? 'N/A' : new Date(asset.lastModified).toLocaleString()}
              </div>
            ),
            minWidth: 100,
          },
          {
            Header: 'Release Tags',
            headerAlign: 'center',
            id: 'tags',
            accessor: (asset) => <div style={{ textAlign: 'center' }}>{getAssetOs(asset) || 'N/A'}</div>,
            minWidth: 100,
          },
          {
            Header: 'Download Count',
            headerAlign: 'center',
            id: 'assetCount',
            // TODO: Replace with actual download count when available right now we have no way of getting this we will need to see if we can using R2 api
            accessor: () => <div style={{ textAlign: 'center' }}>0</div>,
          },
        ]}
        rowActions={{
          actions: [
            {
              Icon: () => <Icon name='download' />,
              label: 'Download Asset',
              onClick: (asset) => {
                const platform: 'mac' | 'win32' | 'linux' =
                  getAssetOs(asset) === 'darwin'
                    ? 'mac'
                    : getAssetOs(asset) === 'win32'
                    ? 'win32'
                    : getAssetOs(asset) === 'linux'
                    ? 'linux'
                    : 'linux';
                handleDownload(platform, asset.key);
              },
            },
            {
              Icon: () => <Icon name='delete' />,
              label: 'Delete Asset',
              onClick: (asset) => {
                setDeletingAsset(asset.key);
                openModal();
              },
            },
          ],
        }}
        isPaginated
        hasGlobalSearch
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: 'Refresh',
            onClick: () => {
              window.location.reload();
            },
          },
        ]}
      />
      <ConfirmationModal
        {...modalProps}
        title={`Delete Asset:`}
        subLabel={deletingAsset}
        message='Are you sure you want to delete this asset? This action cannot be undone.'
        destructive
        onConfirm={() => {
          handleDelete(deletingAsset, version);
        }}
        onCancel={closeModal}
      />
    </>
  );
};
